import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal, Select, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  pw98m5Api,
  ReverseCandidate,
  SubFormType,
  SUB_FORM_LABELS,
} from '../../services/pw98m5Api';
import './PW98M5Form.css';

const { Option } = Select;

// ---- Item state with selection ----
interface ItemState {
  item: ReverseCandidate;
  selected: boolean;
}

// ---- Component ----
const PW98M5Form: React.FC = () => {
  const navigate = useNavigate();
  const [subForm, setSubForm] = useState<SubFormType>('fa');
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [queried, setQueried] = useState(false);

  // ---- Derived state ----
  const selectedCount = items.filter((s) => s.selected).length;
  const allSelected = items.length > 0 && items.every((s) => s.selected);
  const someSelected = items.some((s) => s.selected) && !allSelected;

  // ---- Query ----
  const onQuery = useCallback(async () => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await pw98m5Api.queryCandidates(subForm, startNo, endNo);
      const mapped: ItemState[] = (res.items || []).map((item) => ({
        item,
        selected: false,
      }));
      setItems(mapped);
      setQueried(true);
      if (mapped.length === 0) {
        message.info('查無符合條件的人事申請單');
      } else {
        message.success(`查詢到 ${mapped.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [subForm, startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  // ---- Toggle selection ----
  const handleToggle = useCallback((docNo: string, checked: boolean) => {
    setItems((prev) =>
      prev.map((s) =>
        s.item.doc_no === docNo ? { ...s, selected: checked } : s
      )
    );
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    setItems((prev) => prev.map((s) => ({ ...s, selected: checked })));
  }, []);

  // ---- Execute reversal ----
  const onExecute = useCallback(() => {
    if (selectedCount === 0) {
      message.warning('請先勾選要沖銷的人事申請單');
      return;
    }
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷已勾選的 ${selectedCount} 筆人事申請單？此操作不可復原。`,
      okText: '確定沖銷',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        const selectedNos = items
          .filter((s) => s.selected)
          .map((s) => s.item.doc_no);
        setExecuting(true);
        try {
          const res = await pw98m5Api.reverse(subForm, selectedNos);
          if (res.failed === 0) {
            message.success(res.message || `沖銷完成，成功 ${res.success} 筆`);
          } else {
            message.warning(
              res.message || `成功 ${res.success} 筆，失敗 ${res.failed} 筆`
            );
            if (res.errors && res.errors.length > 0) {
              res.errors.forEach((err) => message.error(err));
            }
          }
          setItems([]);
          setQueried(false);
        } catch (e: unknown) {
          const err = e as { response?: { data?: { error?: string } } };
          message.error(err.response?.data?.error || '沖銷執行失敗');
        } finally {
          setExecuting(false);
        }
      },
    });
  }, [selectedCount, items, subForm]);

  // ---- Table columns ----
  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) => handleToggle(record.item.doc_no, e.target.checked)}
        />
      ),
    },
    {
      title: '申請單號',
      dataIndex: ['item', 'doc_no'],
      key: 'doc_no',
      width: 120,
    },
    {
      title: '申請人代碼',
      dataIndex: ['item', 'applicant_id'],
      key: 'applicant_id',
      width: 100,
    },
    {
      title: '申請人姓名',
      dataIndex: ['item', 'applicant_name'],
      key: 'applicant_name',
      width: 100,
    },
    {
      title: '部門代碼',
      dataIndex: ['item', 'dept_code'],
      key: 'dept_code',
      width: 90,
    },
    {
      title: '部門名稱',
      dataIndex: ['item', 'dept_name'],
      key: 'dept_name',
      width: 120,
    },
    {
      title: '申請日期',
      dataIndex: ['item', 'apply_date'],
      key: 'apply_date',
      width: 110,
    },
    {
      title: '核准狀態',
      dataIndex: ['item', 'status'],
      key: 'status',
      width: 90,
    },
  ];

  // ---- Render ----
  return (
    <div className="pw98m5-form">
      {(loading || executing) && (
        <div className="loading-overlay">
          {executing ? '沖銷執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                人事管理
              </a>
              <span>/</span>
              <span className="current">人事資料核准沖銷</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>人事資料核准沖銷</h1>
            <span className="badge">PW98M5</span>
          </div>
        </div>

        {/* Query Bar */}
        <div className="query-bar">
          <label>表單類型:</label>
          <Select
            value={subForm}
            onChange={(val) => {
              setSubForm(val);
              setItems([]);
              setQueried(false);
            }}
            style={{ width: 200 }}
          >
            {(Object.keys(SUB_FORM_LABELS) as SubFormType[]).map((key) => (
              <Option key={key} value={key}>
                {SUB_FORM_LABELS[key]}
              </Option>
            ))}
          </Select>
          <label>申請單號 .從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 160 }}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 160 }}
            allowClear
          />
          <Button type="primary" onClick={onQuery} loading={loading}>
            確定範圍
          </Button>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.doc_no}
              pagination={false}
              size="small"
              scroll={{ y: 420 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請選擇表單類型、輸入申請單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executing}
            loading={executing}
            onClick={onExecute}
          >
            存檔 ({selectedCount})
          </Button>
          <Button
            danger
            size="large"
            onClick={() => navigate('/erp')}
          >
            離開
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PW98M5Form;
